/**
 * 催し物ブログ一覧のカスタムフィルター
 *
 * Shopify Liquid は任意の GET パラメータを読めないため、
 * 記事一覧（最大250件）を HTML に出力し、本スクリプトで絞り込み・ページネーションを行う。
 *
 * 絞り込みルール: グループ内 OR / グループ間 AND
 * - 開催月・開催状況: メタフィールド日付（data-start / data-end）
 * - タググループ: ブロック設定タグ（data-tags）
 *
 * URL 例:
 *   ?month=4,5&status=ongoing&g_ブロックID=関東,近畿&fpage=2
 *
 * ※ page ではなく fpage を使う（Shopify の {% paginate %} と page パラメータが競合するため）
 */
(function () {
  // クエリパラメータ名
  const PARAM_MONTH = 'month';       // 開催月（1〜12、カンマ区切り）
  const PARAM_STATUS = 'status';     // 開催状況（ended / ongoing / upcoming）
  const PARAM_PAGE = 'fpage';        // 絞り込み後のページ番号（Shopify paginate の page と区別）
  const GROUP_PREFIX = 'g_';         // タググループ（g_{ブロックID}=タグ名）

  // --- 日付ユーティリティ ---

  /** data-start / data-end（YYYY-MM-DD）を Date に変換 */
  function parseDateString(value) {
    if (!value) return null;
    const parts = value.split('-').map(Number);
    if (parts.length !== 3 || parts.some(Number.isNaN)) return null;
    return new Date(parts[0], parts[1] - 1, parts[2]);
  }

  /** 時刻を 00:00:00 に揃える */
  function startOfDay(date) {
    const copy = new Date(date.getTime());
    copy.setHours(0, 0, 0, 0);
    return copy;
  }

  /** 指定年月の最終日を取得 */
  function endOfMonth(year, month) {
    return new Date(year, month, 0);
  }

  /** イベント期間が指定年月と1日でも重なるか */
  function overlapsMonthRange(start, end, month, year) {
    const rangeStart = startOfDay(start);
    const rangeEnd = startOfDay(end);
    const monthStart = new Date(year, month - 1, 1);
    const monthEnd = endOfMonth(year, month);
    return rangeStart <= monthEnd && rangeEnd >= monthStart;
  }

  /** 今日基準で開催状況を判定（終了 / 開催中 / 開催予定） */
  function getArticleStatus(start, end, today) {
    if (!start) return null;
    const rangeStart = startOfDay(start);
    const rangeEnd = startOfDay(end || start);

    if (rangeEnd < today) return 'ended';
    if (rangeStart > today) return 'upcoming';
    return 'ongoing';
  }

  // --- URL パラメータの読み書き ---

  /** カンマ区切りパラメータを配列に変換 */
  function parseCsvParam(params, key) {
    const raw = params.get(key);
    if (!raw) return [];
    return raw.split(',').map((value) => value.trim()).filter(Boolean);
  }

  /** 開催月パラメータを配列に変換（1〜12のみ） */
  function parseMonthParam(params) {
    return parseCsvParam(params, PARAM_MONTH)
      .map((value) => parseInt(value, 10))
      .filter((month) => month >= 1 && month <= 12);
  }

  /** g_ 接頭辞のタググループパラメータをオブジェクトに変換 */
  function parseTagGroupParams(params) {
    const groups = {};
    params.forEach((value, key) => {
      if (!key.startsWith(GROUP_PREFIX)) return;
      groups[key] = value.split(',').map((tag) => decodeURIComponent(tag.trim())).filter(Boolean);
    });
    return groups;
  }

  /** いずれかのフィルターが選択されているか */
  function hasActiveFilters(params) {
    if (parseMonthParam(params).length > 0) return true;
    if (parseCsvParam(params, PARAM_STATUS).length > 0) return true;
    return Object.keys(parseTagGroupParams(params)).some((key) => params.get(key));
  }

  /** 指定グループの選択値一覧を取得 */
  function getGroupValues(params, group) {
    if (group === 'month') return parseMonthParam(params).map(String);
    if (group === 'status') return parseCsvParam(params, PARAM_STATUS);
    return parseCsvParam(params, group).map((value) => decodeURIComponent(value));
  }

  /** 指定グループの選択値を URL パラメータに書き込む */
  function setGroupValues(params, group, values) {
    if (group === 'month') {
      if (values.length === 0) params.delete(PARAM_MONTH);
      else params.set(PARAM_MONTH, values.join(','));
      return;
    }

    if (group === 'status') {
      if (values.length === 0) params.delete(PARAM_STATUS);
      else params.set(PARAM_STATUS, values.join(','));
      return;
    }

    if (values.length === 0) params.delete(group);
    else params.set(group, values.map((value) => encodeURIComponent(value)).join(','));
  }

  /** チェックボックス用: 1項目の ON/OFF をトグルした遷移先 URL を組み立て */
  function buildToggleUrl(blogUrl, params, group, value) {
    const nextParams = new URLSearchParams(params.toString());
    const values = getGroupValues(nextParams, group);
    const index = values.indexOf(value);
    if (index === -1) values.push(value);
    else values.splice(index, 1);

    setGroupValues(nextParams, group, values);
    nextParams.delete(PARAM_PAGE); // 条件変更時は1ページ目へ

    const query = nextParams.toString();
    return query ? `${blogUrl}?${query}` : blogUrl;
  }

  // --- 記事の絞り込み判定 ---

  /** 1記事が現在のフィルター条件をすべて満たすか（グループ間 AND） */
  function articleMatchesFilters(article, filters, today, currentYear) {
    const tags = (article.dataset.tags || '')
      .split('||')
      .map((tag) => tag.trim())
      .filter(Boolean);
    const start = parseDateString(article.dataset.start);
    const end = parseDateString(article.dataset.end) || start;

    // 開催月（グループ内 OR / 当年・翌年のいずれかに重なればヒット）
    if (filters.months.length > 0) {
      if (!start) return false;
      const monthMatch = filters.months.some((month) =>
        overlapsMonthRange(start, end, month, currentYear) ||
        overlapsMonthRange(start, end, month, currentYear + 1)
      );
      if (!monthMatch) return false;
    }

    // 開催状況（グループ内 OR）
    if (filters.statuses.length > 0) {
      const status = getArticleStatus(start, end, today);
      if (!status || !filters.statuses.includes(status)) return false;
    }

    // タググループ（グループ内 OR / グループ間 AND）
    for (const groupKey of filters.groupKeys) {
      const selectedTags = filters.tagGroups[groupKey] || [];
      if (selectedTags.length === 0) continue;
      const tagMatch = selectedTags.some((tag) => tags.includes(tag));
      if (!tagMatch) return false;
    }

    return true;
  }

  /** URL からフィルター状態オブジェクトを生成 */
  function collectFilterState(params) {
    const tagGroups = parseTagGroupParams(params);
    return {
      months: parseMonthParam(params),
      statuses: parseCsvParam(params, PARAM_STATUS),
      tagGroups,
      groupKeys: Object.keys(tagGroups),
    };
  }

  // --- ページネーション（絞り込み後の件数ベース） ---

  /** 絞り込み結果に応じたページネーション UI を生成 */
  function renderPagination(container, blogUrl, params, currentPage, totalPages) {
    container.innerHTML = '';

    if (totalPages <= 1) return;

    const nav = document.createElement('nav');
    nav.className = 'pagination__nav c-pagination__nav';
    nav.setAttribute('aria-label', 'Pagination');

    const createPageUrl = (page) => {
      const nextParams = new URLSearchParams(params.toString());
      if (page <= 1) nextParams.delete(PARAM_PAGE);
      else nextParams.set(PARAM_PAGE, String(page));
      const query = nextParams.toString();
      return query ? `${blogUrl}?${query}` : blogUrl;
    };

    if (currentPage > 1) {
      const prev = document.createElement('a');
      prev.className = 'c-button c-button__arrow c-button__arrow--left';
      prev.rel = 'prev';
      prev.href = createPageUrl(currentPage - 1);
      prev.innerHTML = '<span class="visually-hidden">前へ</span>';
      nav.appendChild(prev);
    } else {
      const prev = document.createElement('button');
      prev.className = 'c-button c-button__arrow c-button__arrow--left';
      prev.type = 'button';
      prev.disabled = true;
      prev.innerHTML = '<span class="visually-hidden">前へ</span>';
      nav.appendChild(prev);
    }

    const current = document.createElement('span');
    current.className = 'c-pagination__nav-item';
    current.setAttribute('aria-current', 'page');
    current.textContent = String(currentPage);
    nav.appendChild(current);

    const divider = document.createElement('span');
    divider.className = 'c-pagination__nav-item';
    divider.textContent = '/';
    nav.appendChild(divider);

    const last = document.createElement('a');
    last.className = 'c-pagination__nav-item';
    last.href = createPageUrl(totalPages);
    last.textContent = String(totalPages);
    nav.appendChild(last);

    if (currentPage < totalPages) {
      const next = document.createElement('a');
      next.className = 'c-button c-button__arrow c-button__arrow--right';
      next.rel = 'next';
      next.href = createPageUrl(currentPage + 1);
      next.innerHTML = '<span class="visually-hidden">次へ</span>';
      nav.appendChild(next);
    } else {
      const next = document.createElement('button');
      next.className = 'c-button c-button__arrow c-button__arrow--right';
      next.type = 'button';
      next.disabled = true;
      next.innerHTML = '<span class="visually-hidden">次へ</span>';
      nav.appendChild(next);
    }

    container.appendChild(nav);
  }

  // --- 選択中フィルターのピル表示 ---

  const PILL_ICON_SVG =
    '<svg width="9" height="9" viewBox="0 0 9 9" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
    '<path d="M8.49998 8.49998L4.50001 4.50001M4.50001 4.50001L0.5 0.5M4.50001 4.50001L8.50002 0.5M4.50001 4.50001L0.5 8.50002" stroke="#777777" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</svg>';

  /** 現在選択中のフィルター項目一覧を取得（desktop / drawer の重複を除外） */
  function collectActiveFilters(params, filterLinks) {
    const actives = [];
    const seen = new Set();

    filterLinks.forEach((link) => {
      const group = link.dataset.filterGroup;
      const value = link.dataset.filterValue;
      const values = getGroupValues(params, group);
      if (!values.includes(value)) return;

      const key = `${group}:${value}`;
      if (seen.has(key)) return;
      seen.add(key);

      actives.push({
        group,
        value,
        label: link.dataset.filterLabel || value,
      });
    });

    return actives;
  }

  /** 選択中フィルター用ピル（×付き）の DOM を生成 */
  function renderActivePill(label, removeUrl) {
    const item = document.createElement('li');
    item.className = 'c-blog-pills-filter__item';
    item.setAttribute('role', 'listitem');

    const link = document.createElement('a');
    link.href = removeUrl;
    link.className = 'c-blog-pills-filter__pill is-active tap-area';
    link.setAttribute('aria-label', `削除: ${label}`);

    const icon = document.createElement('span');
    icon.className = 'c-blog-pills-filter__icon';
    icon.setAttribute('aria-hidden', 'true');
    icon.innerHTML = PILL_ICON_SVG;

    const text = document.createElement('span');
    text.className = 'c-blog-pills-filter__label';
    text.textContent = label;

    link.appendChild(icon);
    link.appendChild(text);
    item.appendChild(link);
    return item;
  }

  // --- メインクラス ---

  class EventBlogFilter {
    constructor(section) {
      this.section = section;
      this.blogUrl = section.dataset.blogUrl;
      this.perPage = parseInt(section.dataset.articlesPerPage, 10) || 12;
      this.currentYear = parseInt(section.dataset.currentYear, 10) || new Date().getFullYear();
      this.articles = Array.from(section.querySelectorAll('[data-event-article]'));
      this.filterLinks = Array.from(section.querySelectorAll('[data-event-filter-link]'));
      this.clearLinks = Array.from(section.querySelectorAll('[data-event-filter-clear]'));
      this.activeNavs = Array.from(section.querySelectorAll('[data-event-filter-active]'));
      this.emptyEl = section.querySelector('[data-event-filter-empty]');
      this.paginationEl = section.querySelector('[data-event-filter-pagination]');
      this.listEl = section.querySelector('[data-event-filter-list]');
      this.today = startOfDay(new Date());
    }

    /** 初期化: URL を読み取り UI 同期 → 絞り込み適用 */
    init() {
      const params = new URLSearchParams(window.location.search);
      this.syncFilterUi(params);
      this.apply(params);
      this.section.classList.add('is-event-filter-ready');
    }

    /** チェックボックス・ピル・クリアリンクの表示状態を URL と同期 */
    syncFilterUi(params) {
      const active = hasActiveFilters(params);
      const activeFilters = collectActiveFilters(params, this.filterLinks);

      // 各フィルター項目の checked 状態と遷移先 href を更新
      this.filterLinks.forEach((link) => {
        const group = link.dataset.filterGroup;
        const value = link.dataset.filterValue;
        const values = getGroupValues(params, group);
        const isChecked = values.includes(value);
        const checkbox = link.querySelector('.checkbox');

        link.classList.toggle('is-checked', isChecked);
        link.setAttribute('aria-checked', isChecked ? 'true' : 'false');
        link.href = buildToggleUrl(this.blogUrl, params, group, value);

        if (checkbox) {
          checkbox.checked = isChecked;
        }
      });

      // 選択中ピル（PC / SP 両方）を描画
      this.activeNavs.forEach((nav) => {
        const list = nav.querySelector('[data-event-filter-active-list]');
        if (!list) return;

        list.innerHTML = '';
        activeFilters.forEach((filter) => {
          const removeUrl = buildToggleUrl(this.blogUrl, params, filter.group, filter.value);
          list.appendChild(renderActivePill(filter.label, removeUrl));
        });
        nav.hidden = !active;
      });

      this.clearLinks.forEach((link) => {
        link.hidden = !active;
      });
    }

    /** 絞り込みを適用し、記事の表示 / 非表示とページネーションを更新 */
    apply(params) {
      const filters = collectFilterState(params);
      const currentPage = Math.max(1, parseInt(params.get(PARAM_PAGE) || '1', 10));
      const matched = this.articles.filter((article) =>
        articleMatchesFilters(article, filters, this.today, this.currentYear)
      );
      const totalPages = Math.max(1, Math.ceil(matched.length / this.perPage));
      const safePage = Math.min(currentPage, totalPages);
      const startIndex = (safePage - 1) * this.perPage;
      const pageItems = matched.slice(startIndex, startIndex + this.perPage);
      const visibleSet = new Set(pageItems);

      this.articles.forEach((article) => {
        article.hidden = !visibleSet.has(article);
      });

      if (this.listEl) {
        this.listEl.hidden = matched.length === 0;
      }

      if (this.emptyEl) {
        this.emptyEl.hidden = matched.length > 0;
      }

      if (this.paginationEl) {
        renderPagination(this.paginationEl, this.blogUrl, params, safePage, totalPages);
      }
    }
  }

  /** [data-event-filter-section] ごとにフィルターを初期化 */
  function initEventBlogFilters() {
    document.querySelectorAll('[data-event-filter-section]').forEach((section) => {
      new EventBlogFilter(section).init();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initEventBlogFilters);
  } else {
    initEventBlogFilters();
  }
})();
